import React, { useMemo, useState } from 'react';
import { useFormik } from 'formik';
import { MixAndMatchProductContainer } from 'src/MixAndMatchBundle/MixAndMatchProductContainer';
import { MixAndMatchBundleCustomisation } from 'src/MixAndMatchBundle/MixAndMatchBundleCustomisation';
import { MixAndMatchBundleFooter } from 'src/MixAndMatchBundle/MixAndMatchBundleFooter';
import { MixAndMatchBundleHeader } from 'src/MixAndMatchBundle/MixAndMatchBundleHeader';
import { getValidationSchema } from 'src/MixAndMatchBundle/MixAndMatchBundle.schema';
import {
    camelized_wearerBundleDetail_wearerBundleDetail_WearerBundlesProduct_bundle_items,
    camelized_wearerBundleDetail_wearerBundleDetail_WearerBundlesProduct_bundle_items_target_configurable,
    camelized_wearerBundleDetail_wearerBundleDetail_WearerBundlesProduct_locations,
    camelized_wearerBundleDetail_wearerBundleDetail_WearerBundlesProduct_media_gallery,
} from 'src/queries/__generated__/wearerBundleDetail';
import { ProductPrice } from 'src/types/global-types';
import { Customisation } from 'src/components/BundleProduct/types';
import { convertFormDataToPayload } from 'src/MixAndMatchBundle/utils/convertFormDataToPayload';
import { useDispatch } from 'react-redux';
import { useLazyRegister } from 'src/core/engine/hooks/useRegisterFeature';
import { BundleMsg, bundleRegister } from 'src/components/BundleProduct/feature/bundle.register';
import { AppendState } from 'src/components/BundleProduct/feature/bundle.register';
import { AppendState as AppendCartState } from 'src/core/state/cart';
import { DataStatus, MessageType } from 'src/core/engine/types';
import { shallowEqual, useSelector } from 'react-redux';
import { gtmAddToCartWithVersion } from 'src/features/analytics/epics/gtmAddToCartWithVersion';
import { AddBundleVersion } from 'src/features/analytics/utils/eventTypes';
import { WearerOptionMap } from 'src/components/WeareBundleProduct/utils/wearer.types';
import { WearerSummary } from 'src/components/BundleProduct/BundleOptionsProvider';
import { BaseProduct } from 'src/components/ProductDetail/utils/product-detail.types';
import wearerBundleVariants from 'src/queries/wearerBundleVariants.graphql';
import { useQuery } from '@apollo/client';
import { wearerBundleDetail, wearerBundleDetailVariables } from 'src/queries/__generated__/wearerBundleDetail';
import { useDeps } from 'src/hooks/useDeps';
import camelcaseKeys from 'camelcase-keys';
import { BundleTypeValues, BaseBundleType } from 'src/components/BundleProduct/types/index';
import { MessagesModal } from 'src/components/Modal/MessagesModal';

import classes from './MixAndMatchBundle.scss';

export type FormikValuesBundlesType = {
    id: number;
    products: {
        id: number;
        name: string;
        selectedColorsAndSizes: {
            colorId: number;
            sizes: {
                id: number | undefined;
                quantity: number | undefined;
            };
        }[];
    }[];
};

export type FormikValuesType = {
    bundles: FormikValuesBundlesType[];
    customisations: Customisation[];
};

type Props = {
    bundles: camelized_wearerBundleDetail_wearerBundleDetail_WearerBundlesProduct_bundle_items[];
    bundleTitle: string;
    locations: camelized_wearerBundleDetail_wearerBundleDetail_WearerBundlesProduct_locations[];
    prices: ProductPrice;
    shortDescription: { html: string };
    brandBadge?: string;
    mediaGallery: camelized_wearerBundleDetail_wearerBundleDetail_WearerBundlesProduct_media_gallery[];
    sku: string;
    wearerOptions: WearerOptionMap;
    summary: WearerSummary[];
    product: BaseProduct;
    id: number;
};

const selector = (s: AppendState<AppendCartState>) => {
    const itemIds = s.bundle?.itemIds || [];
    return {
        messages: s.bundle?.messages || [],
        status: s.bundle?.status || DataStatus.Idle,
        cartItems: s.cart.items.filter((item) => itemIds.indexOf(item.id) > -1),
    };
};

export const MixAndMatchBundle: React.FC<Props> = ({
    bundles,
    bundleTitle,
    locations,
    prices,
    shortDescription,
    brandBadge,
    mediaGallery,
    sku,
    wearerOptions,
    summary,
    product,
    id,
}) => {
    useLazyRegister(bundleRegister, true, 'BundleSelectionProvider');
    const state = useSelector(selector, shallowEqual);
    const dispatch = useDispatch();
    const [wearerBundleData, setWearerBundleData] = useState<BundleTypeValues[] | BaseBundleType[] | undefined>(
        undefined,
    );

    const { loading, data } = useQuery<wearerBundleDetail, wearerBundleDetailVariables>(wearerBundleVariants, {
        variables: { ids: [id] },
        ssr: useDeps().env.SSR_GQL,
    });

    const basketLoading = state.status === 'Pending' || state.status === 'Success';

    if (state.status === 'Success') {
        gtmAddToCartWithVersion(AddBundleVersion.V1, product, '/basket', { price: prices });
        setTimeout(() => {
            location.href = '/basket';
        }, 1000);
    }

    const initialFormikValues = useMemo(
        () => ({
            bundles: bundles.map((category) => ({
                id: category.optionId,
                products: [],
            })),
            customisations: [],
        }),
        [bundles],
    );

    const camalizedData = camelcaseKeys((data ?? {}) as any, { deep: true }) as unknown as NonNullable<typeof data>;
    const computedBundles = data
        ? bundles?.map((bundle, index) => {
              const computedTarget = bundle?.targetConfigurable.map((target) => {
                  const currentTargetItem = (camalizedData as any)?.wearerBundleDetail?.[0]?.bundleItems[
                      index
                  ].targetConfigurable.find((dataTarget) => dataTarget.id === target.id);

                  return { ...target, variants: currentTargetItem?.variants };
              });
              const computedBundles = { ...bundle };
              computedBundles.targetConfigurable = computedTarget;

              return computedBundles;
          })
        : bundles;

    const formik = useFormik<FormikValuesType>({
        initialValues: initialFormikValues,
        validateOnMount: true,
        onSubmit: (values) => {
            dispatch(BundleMsg('Bundle.Add', convertFormDataToPayload(values, sku, computedBundles, wearerBundleData)));
        },
        validationSchema: getValidationSchema(bundles),
        validateOnChange: true,
    });

    const setFormikValueOfBundles = (bundles: FormikValuesBundlesType[]) => {
        formik.setFieldValue('bundles', bundles);
    };

    const setFormikValueOfCustomisations = async (customisations: Customisation[]) => {
        await formik.setFieldValue('customisations', customisations);
        formik.validateForm();
    };

    const validateFormikForm = () => formik.validateForm();

    const maxQty = bundles.reduce((sum, { maxQty }) => sum + maxQty, 0);

    const getOptions = (
        variant: camelized_wearerBundleDetail_wearerBundleDetail_WearerBundlesProduct_bundle_items_target_configurable,
        name: string,
    ) => variant.configurableOptions.find((option) => option.label === name)?.values;

    const getVariants = (
        category: camelized_wearerBundleDetail_wearerBundleDetail_WearerBundlesProduct_bundle_items,
    ) => {
        const filteredCategory = category.targetConfigurable.filter(
            (variant) => variant.optionId === category.optionId,
        );
        return filteredCategory.map((variant) => ({
            title: variant.name,
            image: variant.smallImage.url,
            id: variant.id,
            value: { colors: getOptions(variant, 'Colour'), sizes: getOptions(variant, 'Sizing') },
            mediaEntries: variant.mediaGallery,
            description: variant.description,
            sizeGuides: variant.sizeGuides,
        }));
    };

    const errorMessage = Boolean(
        state.messages.find((message) => message.text.includes('out of stock') && message.type === MessageType.Error),
    ) && [
        {
            text: 'One of the selected products is currently Out of Stock.\nPlease reload the page.',
            type: MessageType.Error,
        },
    ];

    const [firstPhoto] = mediaGallery || [];

    return (
        <div className={classes.bundleContainer}>
            <MixAndMatchBundleHeader
                title={bundleTitle}
                prices={prices}
                imgSrc={firstPhoto.url}
                shortDescription={shortDescription}
                brandBadge={brandBadge}
            />
            <div className={classes.productsContainer}>
                {bundles.map((category, index) => (
                    <MixAndMatchProductContainer
                        key={category.title}
                        title={category.title}
                        requiredQty={category.maxQty}
                        bundles={[...formik.values.bundles]}
                        setFormikValueOfBundles={setFormikValueOfBundles}
                        validateFormikForm={validateFormikForm}
                        index={index}
                        variants={getVariants(category)}
                        wearerOptions={wearerOptions}
                        summary={summary}
                    />
                ))}
            </div>
            <MixAndMatchBundleCustomisation
                stepNr={bundles.length + 1}
                setFormikValueOfCustomisations={setFormikValueOfCustomisations}
                formikCustomisations={[...formik.values.customisations]}
                maxQty={maxQty}
                isValid={!formik.errors.bundles}
                locations={locations}
                onAddToCartClick={formik.handleSubmit}
                basketLoading={basketLoading}
            />
            <MixAndMatchBundleFooter
                bundleTitle={bundleTitle}
                categories={bundles}
                bundles={[...formik.values.bundles]}
                isFormValid={formik.isValid}
                onClick={formik.handleSubmit}
                basketLoading={basketLoading}
                setWearerBundleData={setWearerBundleData}
                wearerOptions={wearerOptions}
                summary={summary}
                isDataLoaded={Boolean(wearerBundleData)}
                loading={loading}
            />
            {state.status === 'Error' && state.messages.length > 0 && (
                <MessagesModal
                    messages={errorMessage || state.messages}
                    close={() => dispatch(BundleMsg('Bundle.Reset'))}
                    title="An error occurred"
                />
            )}
        </div>
    );
};
